import { ArrowRight, Check, LockKeyhole, ShieldCheck, Smartphone } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api } from "../api";

export function AuthScreen({ setupRequired, connectionError, onAuthenticated }: { setupRequired: boolean; connectionError: string; onAuthenticated: () => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(connectionError);

  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      if (setupRequired) await api.setup(email, password); else await api.login(email, password);
      await onAuthenticated();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to continue"); }
    finally { setBusy(false); }
  }

  return (
    <main className="auth-page">
      <section className="auth-story">
        <div className="sidebar-brand"><span className="brand-mark brand-mark--small">D<span /></span><span><strong>Dayah finance</strong><small>Personal ledger</small></span></div>
        <div className="auth-copy">
          <p className="eyebrow">Money, without the spreadsheet ceremony</p>
          <h1>Know where you stand.<br />Record what just happened.</h1>
          <p>One private place for balances, bills, savings goals, and the everyday spending that changes them.</p>
          <ul>
            <li><Smartphone /><span><strong>Built for the five-second entry</strong>Amount first, then category and account.</span></li>
            <li><ShieldCheck /><span><strong>No banking credentials</strong>Only the records you enter are stored.</span></li>
            <li><Check /><span><strong>Malaysian by default</strong>Ringgit, local dates, Kuala Lumpur time.</span></li>
          </ul>
        </div>
        <small className="auth-footnote">Private single-user application · Secure server session</small>
      </section>
      <section className="auth-form-panel">
        <form onSubmit={submit} className="auth-card">
          <span className="auth-icon"><LockKeyhole /></span>
          <p className="eyebrow">{setupRequired ? "First-time setup" : "Welcome back"}</p>
          <h2>{setupRequired ? "Create your private access" : "Open your finance tracker"}</h2>
          <p>{setupRequired ? "This creates the only user account. Use a unique password with at least 12 characters." : "Sign in to view and update your personal records."}</p>
          <label>Email address<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required /></label>
          <label>Password<input type="password" autoComplete={setupRequired ? "new-password" : "current-password"} minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="At least 12 characters" required /></label>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="primary-button" disabled={busy}>{busy ? "Checking…" : setupRequired ? "Create private account" : "Sign in"}<ArrowRight /></button>
          <small>Session cookies are HTTP-only and never expose your password to the browser.</small>
        </form>
      </section>
    </main>
  );
}
